import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );
  const windowQuery = window.matchMedia('(prefers-color-scheme: dark)');

  const darkModeChange = useCallback(
    (e: MediaQueryListEvent) => {
      const root = window.document.documentElement;

      windowQuery.addEventListener('change', darkModeChange);

      if (theme === 'system') {
        const systemTheme = e.matches ? 'dark' : 'light';
        setBodyTheme(root, systemTheme);
      }
    },
    [theme, windowQuery]
  );

  function setBodyTheme(root: HTMLElement, systemTheme: string) {
    root.classList.remove('light', 'dark');

    root.classList.add(systemTheme);
  }

  useEffect(() => {
    const root = window.document.documentElement;
    windowQuery.addEventListener('change', darkModeChange);

    if (theme !== 'system') {
      setBodyTheme(root, theme);
    } else {
      const systemTheme = windowQuery.matches ? 'dark' : 'light';
      setBodyTheme(root, systemTheme);
    }

    return () => {
      windowQuery.removeEventListener('change', darkModeChange);
    };
  }, [darkModeChange, theme, windowQuery]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
